import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IForumLightWeight, IForumPostEdit } from '@fi-shared/fi-forum/fi-forum';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { FiTextEditorComponent } from '@fi-shared/fi-text-editor/fi-text-editor/fi-text-editor.component';
import { ObjectTypes } from '@fi-shared/types';
import { ILightWeightUser } from '@fi-shared/models';
import { cloneDeep } from '@fi-shared/utils';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-forum-post-edit',
    template: `
        @if (isForumSelectShown) {
            <fi-select-search
                [items]="availableForums"
                [(ngModel)]="forumId"
                (ngModelChange)="onForumIdChange()"
                [label]="'forum.forum_post_in' | fiAppText"
                [displayValue]="'Name'"
                [errorText]="'common_error.mandatory' | fiAppText"
                [matOptionValueProperty]="'Id'"
                [filterBy]="'Name'"
                [required]="true"
            >
            </fi-select-search>
        }

        <mat-form-field appearance="outline" class="fi-forum-post-edit-title">
            <mat-label>{{ titlePlaceholderTextId | fiAppText }}</mat-label>
            <input matInput #editTitleElRef [(ngModel)]="forumPost.Title" [required]="true" />
            <mat-error>{{ 'common_error.mandatory' | fiAppText }}</mat-error>
        </mat-form-field>

        <fi-text-editor
            [(ngModel)]="forumPost.Content"
            [mediaBankFileReferences]="forumPost.MediaBankFileReferences"
            (mediaBankFileReferencesChange)="forumPost.MediaBankFileReferences = $event"
            [linkAttachments]="forumPost.Links"
            (linkAttachmentsChange)="forumPost.Links = $event"
            [placeholder]="'message' | fiAppText"
            [required]="true"
            [alwaysShowActions]="true"
            [saveDisabled]="!forumPost.Title || !forumId"
            [autofocus]="autofocus"
            [objectType]="ObjectTypes.Forum"
            [taggedUsers]="forumPost.TaggedUsers"
            (saveClick)="saveForumPost()"
            (cancelClick)="editCancelled.emit()"
        >
        </fi-text-editor>
    `,
    styleUrls: ['./fi-forum-post-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-forum-post-edit' },
    standalone: false,
})
export class FiForumPostEditComponent implements OnInit {
    @Input() isForumSelectShown: boolean = true;
    @Input() forumPost: IForumPostEdit;
    @Input() forumId: number;
    @Output() forumIdChange = new EventEmitter<number>();
    @Input() availableForums: IForumLightWeight[] = [];
    @Input() autofocus: boolean;
    @Input() titlePlaceholderTextId = 'common.title';

    @Output() forumPostSaved = new EventEmitter<IForumPostEdit>();
    @Output() editCancelled = new EventEmitter();

    @ViewChild(FiTextEditorComponent, { static: true }) textEditorComponent: FiTextEditorComponent;
    @ViewChild('editTitleElRef', { static: true }) editTitleElRef: ElementRef<HTMLElement>;

    ObjectTypes = ObjectTypes;

    constructor(
        private fiLoading: LoadingService,
        private forumService: FiForumSharedService,
        private userDataService: FiUserDataService,
        private textEditorService: TextEditorService,
        private textEditorTagUsersService: TextEditorTagUsersService,
    ) {}

    ngOnInit(): void {}

    onForumIdChange() {
        this.forumPost.ForumId = this.forumId;
        this.forumIdChange.emit(this.forumId);
    }

    saveForumPost() {
        const forumPostForSave: IForumPostEdit = cloneDeep(this.forumPost);
        let content = this.textEditorTagUsersService.getConvertedFromEditToViewModeUserTags(
            this.forumPost.Content || '',
        );
        forumPostForSave.Content = this.textEditorService.removeEmptyLinesFromEnd(content);

        this.fiLoading.trackPromise(this.forumService.saveForumPost(forumPostForSave)).then((forumPostId) => {
            if (!forumPostForSave.Id) {
                const currentUser = this.userDataService.getUserData();
                forumPostForSave.CreatedByUserId = currentUser.UserId;
                forumPostForSave.CreatedByUserName = currentUser.FullName;
                forumPostForSave.CreatedOn = forumPostForSave.ChangedOn = new Date().toISOString();
            }
            forumPostForSave.Id = forumPostId;
            this.forumPostSaved.next(forumPostForSave);
        });
    }

    focusTitleInput() {
        this.editTitleElRef.nativeElement.focus();
    }
}
